import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    CircularProgress,
    Tooltip,
    alpha,
    useTheme,
    Link,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    PlayArrow as PlayArrowIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Schedule as ScheduleIcon,
    HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import analyticsService from '../../../services/analyticsApi';
import { useFilters } from '../../../contexts/FiltersContext';
import { useAnalyticsData } from '../../../contexts/AnalyticsDataContext';

const CHART_COLORS = {
    primary: '#90BF45',
    secondary: '#6C757D',
    success: '#28a745',
    info: '#17a2b8',
    warning: '#ffc107',
    danger: '#dc3545',
    optionBG: '#eff6e9'
};

interface ExportJob {
    id: number;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    created_at: string;
    file_url?: string;
    file_name?: string;
    // Failure reason from the API (e.g. "Export too large: 175,347 shipments …").
    error?: string;
}

interface ExportJobsWidgetProps {
    onExportStart?: () => void;
    onSuccess?: (message: string) => void;
    onError?: (message: string) => void;
}

const ExportJobsWidget: React.FC<ExportJobsWidgetProps> = ({
    onExportStart,
    onSuccess,
    onError,
}) => {
    const theme = useTheme();
    const useFilterContext = useFilters();
    const { activeTabId } = useAnalyticsData();

    const [jobs, setJobs] = useState<ExportJob[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isStartingExport, setIsStartingExport] = useState(false);

    useEffect(() => {
        fetchJobs();
    }, []);

    const fetchJobs = async () => {
        setIsLoading(true);
        try {
            const { data } = await analyticsService.getData(
                analyticsService.endpoints.getShipmentsExports
            );

            if (data.code === 200 && Array.isArray(data.response)) {
                setJobs(data.response);
            } else {
                setJobs([]);
            }
        } catch (error) {
            console.error('Failed to fetch export jobs:', error);
            setJobs([]);
            if (onError) {
                onError('Failed to fetch export jobs');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleStartExport = async () => {
        setIsStartingExport(true);
        try {
            const payload = useFilterContext.buildFilterPayload({
                tab_id: activeTabId,
                tab_type: activeTabId
            });
            delete payload.tab_id;
            delete payload.tab_type;
            delete payload.transportMode;

            const { data } = await analyticsService.postData(
                analyticsService.endpoints.generateShipmentsExcelExport,
                payload
            );

            if (onSuccess) {
                onSuccess(data.message || 'Export job started successfully.');
            }

            // Refresh the jobs list after starting export
            setTimeout(() => {
                fetchJobs();
            }, 1000);

            if (onExportStart) {
                onExportStart();
            }
        } catch (error) {
            console.error('Failed to start export:', error);
            if (onError) {
                onError('Failed to start export job.');
            }
        } finally {
            setIsStartingExport(false);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <CheckCircleIcon sx={{ color: CHART_COLORS.success, fontSize: 20 }} />;
            case 'failed':
                return <CancelIcon sx={{ color: CHART_COLORS.danger, fontSize: 20 }} />;
            case 'processing':
                return <HourglassEmptyIcon sx={{ color: CHART_COLORS.warning, fontSize: 20 }} />;
            case 'pending':
                return <ScheduleIcon sx={{ color: CHART_COLORS.secondary, fontSize: 20 }} />;
            default:
                return <ScheduleIcon sx={{ color: CHART_COLORS.secondary, fontSize: 20 }} />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return CHART_COLORS.success;
            case 'failed':
                return CHART_COLORS.danger;
            case 'processing':
                return CHART_COLORS.warning;
            case 'pending':
                return CHART_COLORS.secondary;
            default:
                return CHART_COLORS.secondary;
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Paper
            elevation={8}
            sx={{
                width: 550,
                maxHeight: 500,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px) saturate(180%)',
                border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            }}
        >
            {/* Header with Start Export Button */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: `linear-gradient(135deg, ${alpha(CHART_COLORS.primary, 0.06)}, transparent)`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <DownloadIcon sx={{ color: CHART_COLORS.primary }} />
                    Export Jobs
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Refresh jobs list">
                        <IconButton
                            size="small"
                            onClick={fetchJobs}
                            disabled={isLoading}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    color: CHART_COLORS.primary,
                                    background: alpha(CHART_COLORS.primary, 0.1),
                                },
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>

                    <Button
                        variant="contained"
                        size="small"
                        startIcon={isStartingExport ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                        onClick={handleStartExport}
                        disabled={isStartingExport}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: CHART_COLORS.primary,
                            '&:hover': {
                                bgcolor: alpha(CHART_COLORS.primary, 0.85),
                            },
                        }}
                    >
                        Start Export
                    </Button>
                </Box>
            </Box>

            {/* Jobs Table */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {(() => {
                  if (isLoading) {
                    return (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 8,
                        }}
                    >
                        <CircularProgress
                            sx={{
                                color: CHART_COLORS.primary,
                                '& .MuiCircularProgress-circle': {
                                    strokeLinecap: 'round',
                                },
                            }}
                        />
                    </Box>
                    );
                  }
                  if (jobs.length === 0) {
                    return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 8,
                            px: 3,
                        }}
                    >
                        <DownloadIcon
                            sx={{
                                fontSize: 48,
                                color: 'text.disabled',
                                mb: 2,
                            }}
                        />
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            No export jobs yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                            Click "Start Export" to create your first export
                        </Typography>
                    </Box>
                    );
                  }
                  return (
                    <TableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            background: alpha(CHART_COLORS.optionBG, 1),
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            color: theme.palette.text.primary,
                                            borderBottom: `2px solid ${CHART_COLORS.primary}`,
                                        }}
                                    >
                                        Job ID
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            background: alpha(CHART_COLORS.optionBG, 1),
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            color: theme.palette.text.primary,
                                            borderBottom: `2px solid ${CHART_COLORS.primary}`,
                                        }}
                                    >
                                        Status
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            background: alpha(CHART_COLORS.optionBG, 1),
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            color: theme.palette.text.primary,
                                            borderBottom: `2px solid ${CHART_COLORS.primary}`,
                                        }}
                                    >
                                        Created At
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            background: alpha(CHART_COLORS.optionBG, 1),
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            color: theme.palette.text.primary,
                                            borderBottom: `2px solid ${CHART_COLORS.primary}`,
                                        }}
                                    >
                                        Download
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {jobs.map((job, index) => (
                                    <TableRow
                                        key={job.id}
                                        sx={{
                                            transition: 'all 0.2s ease-in-out',
                                            '&:nth-of-type(even)': {
                                                background: alpha(theme.palette.divider, 0.02),
                                            },
                                            '&:hover': {
                                                background: alpha(CHART_COLORS.primary, 0.05),
                                                transform: 'scale(1.002)',
                                            },
                                        }}
                                    >

                                        <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                            {job.id}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getStatusIcon(job.status)}
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: 600,
                                                        color: getStatusColor(job.status),
                                                        textTransform: 'capitalize',
                                                    }}
                                                >
                                                    {job.status}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.875rem' }}>
                                            {formatDate(job.created_at)}
                                        </TableCell>
                                        <TableCell align="center">
                                            {(() => {
                                                if (job.status === 'completed' && job.file_url) {
                                                    return (
                                                        <Tooltip title={job.file_name}>
                                                            <IconButton
                                                                size="small"
                                                                component={Link}
                                                                href={job.file_url}
                                                                download={job.file_name}
                                                                sx={{
                                                                    color: CHART_COLORS.primary,
                                                                    '&:hover': {
                                                                        background: alpha(CHART_COLORS.primary, 0.1),
                                                                    },
                                                                }}
                                                            >
                                                                <DownloadIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    );
                                                }
                                                if (job.status === 'failed' && job.error) {
                                                    return (
                                                        <Tooltip title={job.error}>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: CHART_COLORS.danger,
                                                                    display: 'block',
                                                                    whiteSpace: 'normal',
                                                                    textAlign: 'left',
                                                                    lineHeight: 1.3,
                                                                }}
                                                            >
                                                                {job.error}
                                                            </Typography>
                                                        </Tooltip>
                                                    );
                                                }
                                                return (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                                                    >
                                                        -
                                                    </Typography>
                                                );
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    );
                })()}
            </Box>
        </Paper >
    );
};

export default ExportJobsWidget;
